import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button, CabinetTablePageSkeleton, useToast } from '@shared/ui';
import { hasPermission } from '@entities/access';
import { useAuth } from '@entities/auth';
import {
  DEFAULT_FILTERS,
  type ServiceAccountingFilters,
} from '@entities/service-accounting';
import { useServiceAccountings } from '@features/service-accounting';
import { SummaryCards } from '@widgets/service-accounting/summary-cards';
import { InfoBanner } from '@widgets/service-accounting/info-banner';
import { FiltersToolbar } from '@widgets/service-accounting/filters-toolbar';
import { Legend } from '@widgets/service-accounting/legend';
import { OperationList } from '@widgets/service-accounting/operation-list';
import { CreateOperationModal } from '@widgets/service-accounting/create-operation-modal';

const PAGE_LIMIT = 35;

export function ServiceAccountingPage() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { user, isLoading: isAuthLoading } = useAuth();
  const isAdmin = hasPermission(user, 'accounting.read_any');

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ServiceAccountingFilters>(DEFAULT_FILTERS);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { operations, summary, pagination, isLoading, error, mutate } = useServiceAccountings(
    page,
    PAGE_LIMIT,
    filters
  );

  const handleFiltersChange = (next: ServiceAccountingFilters) => {
    setFilters(next);
    setPage(1);
  };

  const handleRefresh = () => {
    void mutate();
  };

  const handleCreateSuccess = () => {
    void mutate();
  };

  useEffect(() => {
    if (error) {
      toast(t('serviceAccounting.loadError'), 'error');
    }
  }, [error, t, toast]);

  if (isAuthLoading) {
    return <CabinetTablePageSkeleton statCount={3} />;
  }

  if (!isAdmin) {
    return <Navigate to="/balance" replace />;
  }

  return (
    <div className="page-stack min-w-0">
      <div className="flex justify-end">
        <Button
          variant="default"
          className="page-action-button"
          onClick={() => setIsCreateOpen(true)}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          {t('serviceAccounting.create')}
        </Button>
      </div>

      <InfoBanner />

      <SummaryCards summary={summary} isLoading={isLoading && !operations.length} />

      <FiltersToolbar filters={filters} onChange={handleFiltersChange} />

      <Legend />

      <OperationList
        operations={operations}
        isLoading={isLoading}
        pagination={pagination}
        onPageChange={setPage}
        onRefresh={handleRefresh}
      />

      <CreateOperationModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
